import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { cn } from '@/lib/cn';
import { Can } from '@/lib/permissions';
import { recognitionDay } from '@/lib/recognitionDate';
import { useListHref } from '@/hooks/useListLocation';
import { defaultsOf, useUrlState } from '@/hooks/useUrlState';
import { CreateNightModal } from './CreateNightModal';
import { nightCalendarUrlSchema, type CalendarView } from './nightCalendarUrlState';
import { NightsViewToggle } from './NightsViewToggle';

const NightsCalendarDoc = graphql(/* GraphQL */ `
  query NightsCalendar($filter: NightFilter, $pageSize: Int) {
    nights(
      filter: $filter
      page: 1
      pageSize: $pageSize
      sort: { field: STARTS_AT, direction: ASC }
    ) {
      nodes {
        id
        title
        startsAt
        published
        requireTicket
        ticketText
        dresscode
        isActive
      }
      pageInfo {
        totalRows
      }
    }
  }
`);


interface CalNight {
  id: string;
  title: string;
  startsAt: string;
  published: boolean;
  requireTicket: boolean;
  isActive: boolean;
}

/**
 * Night planning calendar (5.3). Hand-built month + weekday grids with
 * date-fns — react-day-picker is a date picker, not an event calendar with
 * titled chips, so we don't pull in a heavier calendar library.
 */
export function NightCalendarPage() {
  const navigate = useNavigate();
  const { state: url, patch } = useUrlState(nightCalendarUrlSchema);
  const listHref = useListHref();
  const cursor = useMemo(() => parseISO(url.date), [url.date]);
  const view = url.view;
  const includeInactive = url.inactive;
  const [createFor, setCreateFor] = useState<Date | null>(null);

  /** Move the cursor from the date the URL carries right now (two quick clicks must both land). */
  const moveCursor = (step: (day: Date, view: CalendarView) => Date) =>
    patch((current) => ({ date: format(step(parseISO(current.date), current.view), 'yyyy-MM-dd') }));

  const range = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(cursor, { weekStartsOn: 1 });
      const end = endOfWeek(cursor, { weekStartsOn: 1 });
      return { from: format(start, 'yyyy-MM-dd'), to: format(end, 'yyyy-MM-dd'), start, end };
    }
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return { from: format(start, 'yyyy-MM-dd'), to: format(end, 'yyyy-MM-dd'), start, end };
  }, [cursor, view]);

  const { data, previousData, loading, error, refetch } = useQuery(NightsCalendarDoc, {
    variables: {
      filter: {
        startsAt: { from: range.from, to: range.to },
        isActive: includeInactive ? null : true,
      },
      pageSize: 200,
    },
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;
  const nights: CalNight[] = useMemo(
    () => viewData?.nights.nodes ?? [],
    [viewData],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, CalNight[]>();
    for (const p of nights) {
      const key = format(new Date(p.startsAt), 'yyyy-MM-dd');
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return map;
  }, [nights]);

  const days = useMemo(
    () => eachDayOfInterval({ start: range.start, end: range.end }),
    [range],
  );

  const recognitionToday = recognitionDay();
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Night calendar
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Dated nights for this venue. Blueprints have no date and live
            under Blueprints.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <NightsViewToggle layout="calendar" />
          <Can permission="night:manage">
            <Button
              id="admin.nights.add"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setCreateFor(cursor)}
            >
              Add night
            </Button>
          </Can>
        </div>
      </div>

      {error ? (
        <QueryErrorNotice error={error} title="Could not load nights" />
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-card dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              id="admin.nights.calendar.prev"
              onClick={() => moveCursor((d, v) => (v === 'month' ? subMonths(d, 1) : subDays(d, 7)))}
              aria-label="Previous"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 id="admin.nights.calendar.header" className="min-w-48 text-center text-sm font-semibold text-gray-900 dark:text-white">
              {view === 'month'
                ? format(cursor, 'MMMM yyyy')
                : `${format(range.start, 'd MMM')} – ${format(range.end, 'd MMM yyyy')}`}
            </h2>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              id="admin.nights.calendar.next"
              onClick={() => moveCursor((d, v) => (v === 'month' ? addMonths(d, 1) : addDays(d, 7)))}
              aria-label="Next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <Button
              id="admin.nights.calendar.today"
              variant="outline"
              size="sm"
              onClick={() => patch({ date: defaultsOf(nightCalendarUrlSchema).date })}
            >
              Today
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input
                id="admin.nights.includeInactive"
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => patch({ inactive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-brand-500"
              />
              Include inactive
            </label>
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-white/10">
              {(['month', 'week'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  id={`admin.nights.calendar.${v}`}
                  onClick={() => patch({ view: v })}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium capitalize',
                    view === v
                      ? 'bg-brand-500 text-white'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
                  )}
                >
                  {v === 'week' ? 'Weekday' : 'Month'}
                </button>
              ))}
            </div>
          </div>
        </header>

        {loading && !viewData ? (
          <p className="p-6 text-sm text-gray-500">Loading calendar…</p>
        ) : (
          <div className="grid grid-cols-7">
            {weekdays.map((d) => (
              <div
                key={d}
                className="border-b border-gray-200 px-2 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:border-white/10"
              >
                {d}
              </div>
            ))}
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const items = byDay.get(key) ?? [];
              const outside = view === 'month' && !isSameMonth(day, cursor);
              const today = isSameDay(day, recognitionToday);
              return (
                <div
                  key={key}
                  className={cn(
                    'relative min-h-28 border-b border-r border-gray-100 p-1.5 text-left align-top last:border-r-0 dark:border-white/5',
                    outside && 'bg-gray-50 dark:bg-gray-950/40',
                    view === 'week' && 'min-h-64',
                  )}
                >
                  <button
                    type="button"
                    className="absolute inset-0"
                    aria-label={`Create night on ${key}`}
                    onClick={() => setCreateFor(day)}
                  />
                  <span
                    aria-current={today ? 'date' : undefined}
                    className={cn(
                      'pointer-events-none relative z-10 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                      today
                        ? 'bg-brand-500 text-white'
                        : outside
                          ? 'text-gray-400'
                          : 'text-gray-800 dark:text-gray-100',
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                  <div className="relative z-10 mt-1 flex flex-col gap-1">
                    {items.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/nights/${p.id}`, { state: { from: listHref } });
                        }}
                        className={cn(
                          'truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium',
                          !p.isActive
                            ? 'bg-gray-100 text-gray-400 line-through dark:bg-gray-800 dark:text-gray-500'
                            : p.published
                              ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                        )}
                        title={p.isActive ? p.title : `${p.title} (inactive)`}
                      >
                        {p.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateNightModal
        open={createFor !== null}
        startsAt={createFor}
        onClose={() => setCreateFor(null)}
        onCreated={async (id) => {
          setCreateFor(null);
          await refetch();
          navigate(`/nights/${id}`, { state: { from: listHref } });
        }}
      />
    </div>
  );
}
